import { useState } from 'react';
import { useSearchParamsWithApiKey, useDebouncedEffect } from '../../tools';
import type { SelectorOption } from '../common';
import { DEFAULT_CULTURE_OPTION, DEFAULT_PANEL_SELECTOR_OPTION, PRIORITY_OPTIONS } from '../../constants';

export enum Culture {
    EnUs = 'en-us',
    FrFr = 'fr-fr',
    DeDe = 'de-de',
    NlNl = 'nl-nl',
    EsEs = 'es-es',
    ItIt = 'it-it',
    EnCa = 'en-ca',
    EnIn = 'en-in',
    EnSg = 'en-sg',
    EnAu = 'en-au',
    EnNz = 'en-nz',
    EnGb = 'en-gb',
    EnIe = 'en-ie',
}

export enum CultureV4 {
    EnUs = 'en-us',
    FrFr = 'fr-fr',
    DeDe = 'de-de',
    NlNl = 'nl-nl',
    EsEs = 'es-es',
    ItIt = 'it-it',
    EnCa = 'en-ca',
    EnIn = 'en-in',
    EnSg = 'en-sg',
    EnAu = 'en-au',
    EnNz = 'en-nz',
    EnGb = 'en-gb',
    EnIe = 'en-ie',
    PtPt = 'pt-pt',
    DaDk = 'da-dk',
    FiFi = 'fi-fi',
    NbNo = 'nb-no',
    SvSe = 'sv-se',
}

interface UseGenerateParams {
    assets: any;
    sourceDocumentUrl: string;
    surfaceSpecificationUrl: string;
    surfaceSpecification: any;
    productName: string;
    panels: SelectorOption;
    dpiThreshold: number | undefined;
    culture: SelectorOption;
    priority: SelectorOption;
    brandProfileUrl: string;
    testingClient: string;
    layout: any;
    targetLargeFontSizeInPt: string;
    substrateColor: string;
    isVersion4: boolean;
    enableMulticolorLogoContrastFix: boolean;
    enableMulticolorLogoOutlineContrastFix: boolean;
    enableEmbroideryLogoContrastFix: boolean;
    enableGeneratePatternDocument: boolean;
    setAssets: (assets: any) => void;
    setSourceDocumentUrl: (url: string) => void;
    setSurfaceSpecificationUrl: (url: string) => void;
    setSurfaceSpecification: (surfaceSpec: any) => void;
    setProductName: (productName: string) => void;
    setPanels: (panels: SelectorOption) => void;
    setDpiThreshold: (dpiThreshold: number | undefined) => void;
    setPriority: (priority: SelectorOption) => void;
    setBrandProfileUrl: (brandProfileUrl: string) => void;
    setTestingClient: (testingClient: string) => void;
    setLayout: (layout: any) => void;
    setTargetLargeFontSizeInPt: (targetLargeFontSizeInPt: string) => void;
    setCulture: (culture: SelectorOption) => void;
    setSubstrateColor: (substrateColor: string) => void;
    setIsVersion4: (isVersion4: boolean) => void;
    setEnableMulticolorLogoContrastFix: (enableMulticolorLogoContrastFix: boolean) => void;
    setEnableMulticolorLogoOutlineContrastFix: (enableMulticolorLogoOutlineContrastFix: boolean) => void;
    setEnableEmbroideryLogoContrastFix: (enableEmbroideryLogoContrastFix: boolean) => void;
    setEnableGeneratePatternDocument: (enableGeneratePatternDocument: boolean) => void;
}

export const useGenerateParams = (): UseGenerateParams => {
    const [searchParams, setSearchParams] = useSearchParamsWithApiKey();

    const assetsParam = searchParams.get('assets');
    const surfaceSpecificationParam = searchParams.get('surfaceSpecification');
    const surfaceSpecificationUrlParam = searchParams.get('surfaceSpecificationUrl');
    const sourceDocumentUrlParam = searchParams.get('sourceDocumentUrl');
    const brandProfileUrlParam = searchParams.get('brandProfileUrl');
    const productNameParam = searchParams.get('productName');
    const testingClientParam = searchParams.get('testingClient');
    const layoutParam = searchParams.get('layout');
    const targetLargeFontSizeInPtParam = searchParams.get('targetLargeFontSizeInPt');
    const panelsQueryParam = searchParams.get('panels');
    const dpiThresholdParam = searchParams.get('dpiThreshold');
    const cultureParam = searchParams.get('culture');
    const priorityParam = searchParams.get('priority');
    const substrateColorParam = searchParams.get('substrateColor');
    const isVersion4Param = searchParams.get('isVersion4');
    const enableMulticolorLogoContrastFixParam = searchParams.get('enableMulticolorLogoContrastFix');
    const enableMulticolorLogoOutlineContrastFixParam = searchParams.get('enableMulticolorLogoOutlineContrastFix');
    const enableEmbroideryLogoContrastFixParam = searchParams.get('enableEmbroideryLogoContrastFix');
    const enableGeneratePatternDocumentParam = searchParams.get('enableGeneratePatternDocument');
    const [targetLargeFontSizeInPt, setTargetLargeFontSizeInPt] = useState(targetLargeFontSizeInPtParam || '');
    const [layout, setLayout] = useState(layoutParam ? JSON.parse(layoutParam) : undefined);
    const [assets, setAssets] = useState(assetsParam ? JSON.parse(assetsParam) : undefined);
    const [surfaceSpecificationUrl, setSurfaceSpecificationUrl] = useState(surfaceSpecificationUrlParam || '');
    const [surfaceSpecification, setSurfaceSpecification] = useState(
        surfaceSpecificationParam ? JSON.parse(surfaceSpecificationParam) : undefined,
    );
    const [sourceDocumentUrl, setSourceDocumentUrl] = useState(sourceDocumentUrlParam || '');
    const [brandProfileUrl, setBrandProfileUrl] = useState(brandProfileUrlParam || '');
    const [testingClient, setTestingClient] = useState(testingClientParam || '');
    const [productName, setProductName] = useState(productNameParam || '');
    const [panels, setPanels] = useState<SelectorOption>(
        panelsQueryParam ? { label: panelsQueryParam, value: panelsQueryParam } : DEFAULT_PANEL_SELECTOR_OPTION,
    );

    const [dpiThreshold, setDpiThreshold] = useState(
        dpiThresholdParam ? Number.parseFloat(dpiThresholdParam) : undefined,
    );
    const [culture, setCulture] = useState<SelectorOption>(
        cultureParam ? { label: cultureParam, value: cultureParam } : DEFAULT_CULTURE_OPTION,
    );
    const [priority, setPriority] = useState<SelectorOption>(
        priorityParam ? { label: priorityParam, value: priorityParam } : PRIORITY_OPTIONS[1],
    );
    const [substrateColor, setSubstrateColor] = useState(substrateColorParam || '');
    const [isVersion4, setIsVersion4] = useState<boolean>(() => {
        if (isVersion4Param === null || isVersion4Param === '') {
            return true;
        }
        return isVersion4Param === 'true';
    });
    const [enableMulticolorLogoContrastFix, setEnableMulticolorLogoContrastFix] = useState(
        enableMulticolorLogoContrastFixParam === 'true',
    );
    const [enableMulticolorLogoOutlineContrastFix, setEnableMulticolorLogoOutlineContrastFix] = useState(
        enableMulticolorLogoOutlineContrastFixParam === 'true',
    );
    const [enableEmbroideryLogoContrastFix, setEnableEmbroideryLogoContrastFix] = useState(
        enableEmbroideryLogoContrastFixParam === 'true',
    );
    const [enableGeneratePatternDocument, setEnableGeneratePatternDocument] = useState(
        enableGeneratePatternDocumentParam === 'true',
    );

    useDebouncedEffect(
        () => {
            const searchParamsObj: Record<string, string> = {};

            if (surfaceSpecificationUrl) {
                searchParamsObj['surfaceSpecificationUrl'] = surfaceSpecificationUrl;
            }

            if (surfaceSpecification) {
                searchParamsObj['surfaceSpecification'] = JSON.stringify(surfaceSpecification);
            }

            if (sourceDocumentUrl) {
                searchParamsObj['sourceDocumentUrl'] = sourceDocumentUrl;
            }

            if (assets) {
                searchParamsObj['assets'] = JSON.stringify(assets);
            }

            if (productName) {
                searchParamsObj['productName'] = productName;
            }

            if (panels) {
                searchParamsObj['panels'] = panels.value;
            }

            if (dpiThreshold) {
                searchParamsObj['dpiThreshold'] = dpiThreshold.toString();
            }

            if (testingClient) {
                searchParamsObj['testingClient'] = testingClient;
            }

            if (layout) {
                searchParamsObj['layout'] = JSON.stringify(layout);
            }

            if (targetLargeFontSizeInPt) {
                searchParamsObj['targetLargeFontSizeInPt'] = targetLargeFontSizeInPt;
            }

            if (culture) {
                searchParamsObj['culture'] = culture.value;
            }

            if (priority) {
                searchParamsObj['priority'] = priority.value;
            }

            if (brandProfileUrl) {
                searchParamsObj['brandProfileUrl'] = brandProfileUrl;
            }

            if (substrateColor) {
                searchParamsObj['substrateColor'] = substrateColor;
            }

            searchParamsObj['isVersion4'] = isVersion4.toString();

            if (enableMulticolorLogoContrastFix) {
                searchParamsObj['enableMulticolorLogoContrastFix'] = enableMulticolorLogoContrastFix.toString();
            }

            if (enableMulticolorLogoOutlineContrastFix) {
                searchParamsObj['enableMulticolorLogoOutlineContrastFix'] =
                    enableMulticolorLogoOutlineContrastFix.toString();
            }

            if (enableEmbroideryLogoContrastFix) {
                searchParamsObj['enableEmbroideryLogoContrastFix'] = enableEmbroideryLogoContrastFix.toString();
            }

            if (enableGeneratePatternDocument) {
                searchParamsObj['enableGeneratePatternDocument'] = enableGeneratePatternDocument.toString();
            }

            setSearchParams(searchParamsObj);
        },
        [
            assets,
            surfaceSpecificationUrl,
            surfaceSpecification,
            sourceDocumentUrl,
            setSearchParams,
            productName,
            dpiThreshold,
            culture,
            priority,
            brandProfileUrl,
            panels,
            testingClient,
            layout,
            targetLargeFontSizeInPt,
            isVersion4,
            substrateColor,
            enableMulticolorLogoContrastFix,
            enableMulticolorLogoOutlineContrastFix,
            enableEmbroideryLogoContrastFix,
            enableGeneratePatternDocument,
        ],
        500,
    );

    return {
        assets,
        surfaceSpecificationUrl,
        surfaceSpecification,
        sourceDocumentUrl,
        productName,
        panels,
        dpiThreshold,
        culture,
        priority,
        brandProfileUrl,
        testingClient,
        layout,
        targetLargeFontSizeInPt,
        substrateColor,
        isVersion4,
        enableMulticolorLogoContrastFix,
        enableMulticolorLogoOutlineContrastFix,
        enableEmbroideryLogoContrastFix,
        enableGeneratePatternDocument,
        setAssets,
        setSurfaceSpecificationUrl,
        setSurfaceSpecification,
        setSourceDocumentUrl,
        setProductName,
        setPanels,
        setDpiThreshold,
        setPriority,
        setBrandProfileUrl,
        setCulture,
        setTestingClient,
        setLayout,
        setTargetLargeFontSizeInPt,
        setSubstrateColor,
        setIsVersion4,
        setEnableMulticolorLogoContrastFix,
        setEnableMulticolorLogoOutlineContrastFix,
        setEnableEmbroideryLogoContrastFix,
        setEnableGeneratePatternDocument,
    };
};
